import React, { useState, useEffect, useContext } from 'react';
import Toggle from 'react-toggle';
// OZNEWS-3136: UncontrolledTooltip dropped from import — its only usage was the commented-out "-stock news" control below.
import { InputGroup, Input, Col, Row, Label, FormGroup, Button } from 'reactstrap';
import { AuthContext } from 'contexts/AuthContext';
import { useDevice } from 'hooks/useMediaQuery';
import { RadioInput } from 'common/Input';
import { CharacterCount } from 'common/CharacterCount';
import { InfoBlock } from 'components/Alerts/components/CreateAndEdit/InfoBlock';
import { TextareaAutocomplete } from './WikiAutocomplete/TextareaAutocomplete';
import { SelectAlertsModal } from './Modals/SelectAlertsModal';

export const SearchForm = ({
    searchValue,
    setSearchValue,
    searchValueError,
    setSearchValueError,
    selectedAlerts,
    clearPreviewForm,
    resetForm,
    useGoogleAlerts,
    setUseGoogleAlerts,
    handleError,
    setErrors,
    inTitlesOnly,
    setInTitlesOnly,
    // OZNEWS-3136: "-stock news" (stop-words) disabled — props no longer used here. Uncomment to restore.
    // useStopWords,
    // setUseStopWords,
    currentId,
    categories,
    selectedCategory,
    setSelectedCategory,
    setSelectedAlerts,
    handleCreatePreview,
    handleAlertSelectSubmit,
    handleClearAlerts,
    errorObj,
    categoriesLoading,
    preSubmitError,
    setPreSubmitError,
    isHasError,
    setIsHasError,
    isEditForm,
    setIsAdvancedSearchShown,
    titleFormVisible,
    previewError,
    children,
}) => {
    const { state } = useContext(AuthContext);
    const { isMobile } = useDevice();
    const [selectAlertsModalOpen, setSelectAlertsModalOpen] = useState(false);
    const [dropdownWidth, setDropdownWidth] = useState(152);
    const [isShowCategories, setIsShowCategories] = useState(false);
    const [isAdvancedSearchActive, setIsAdvancedSearchActive] = useState(false);
    const [isShowSecondGroup, setIsShowSecondGroup] = useState(false);
    const [isShowThirdGroup, setIsShowThirdGroup] = useState(false);
    const [isAnyFirstGroup, setIsAnyFirstGroup] = useState(false);
    const [isAnySecondGroup, setIsAnySecondGroup] = useState(false);
    const [isAnyThirdGroup, setIsAnyThirdGroup] = useState(false);
    const [advancedSearch, setAdvancedSearch] = useState({ first: [], second: [], third: [], exclude: [] });
    const [search, setSearch] = useState(searchValue);
    const [errorMessage, setErrorMessage] = useState('');

    const access = state.profile.organization?.accesses;
    const firstGroupValue = advancedSearch.first?.map((item) => item)?.join(' ') || '';
    const secondGroupValue = advancedSearch.second?.map((item) => item)?.join(' ') || '';
    const thirdGroupValue = advancedSearch.third?.map((item) => item)?.join(' ') || '';
    const excludeValue = advancedSearch.exclude?.map((item) => item)?.join(' ') || '';
    const isHasAccessAdvancedSearch = access?.accessAdvancedSearch;

    useEffect(() => {
        if (access?.accessCategoryInInternalSearch) {
            setIsShowCategories(true);
        }
    }, [state.profile]);

    const toggleSelectAlertsModal = () => setSelectAlertsModalOpen(!selectAlertsModalOpen);
    const handleAutocompleteSelect = (term) => setSearchValue(term);
    const handleInternalSearchValue = (text) => setSearchValue(text);

    const onPressEnter = (e) => {
        if (e?.key === 'Enter') {
            e.preventDefault();
            handleCreatePreview();
        }
    };

    const getTextWidth = (text, font) => {
        const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement('canvas'));
        const context = canvas.getContext('2d');
        context.font = font;
        const metrics = context.measureText(text);
        return metrics.width;
    };

    useEffect(() => {
        if (categories) {
            let foundSelectedCategory;

            if (!selectedCategory || selectedCategory === 'All AI Topics') {
                setDropdownWidth(155);
            } else {
                const allCategories = [];
                categories.forEach((categoryType) => {
                    allCategories.push(...categoryType.categories);
                });
                foundSelectedCategory = allCategories.find((category) => String(category.id) === String(selectedCategory));

                if (!foundSelectedCategory) return;

                if (!isMobile) {
                    setDropdownWidth(getTextWidth('General -  ' + foundSelectedCategory.name, 'normal 14pt sans-serif'));
                }
            }
        }
    }, [categories, selectedCategory]);

    useEffect(() => {
        if (isAdvancedSearchActive) {
            const { first, second, third, exclude } = advancedSearch;

            const firstStr = first.join(' ');
            const secondStr = second.join(' ');
            const thirdStr = third.join(' ');
            const firstGroup =
                `${stringSeparator(firstStr, firstStr)
                    ?.filter((item) => item)
                    ?.join(` ${isAnyFirstGroup ? 'OR ' : 'AND '}`)}` || '';
            const secondGroup =
                `${stringSeparator(secondStr, secondStr)
                    ?.filter((item) => item)
                    ?.join(` ${isAnySecondGroup ? 'OR ' : 'AND '} `)}` || '';
            const thirdGroup =
              `${stringSeparator(thirdStr, thirdStr)
                ?.filter((item) => item)
                ?.join(` ${isAnyThirdGroup ? 'OR ' : 'AND '} `)}` || '';
            const excludeGroup = `${
                exclude?.length ? '-' + exclude?.map((item) => (item.charAt(0) === '-' ? item.slice(1) : item))?.join(' -') : ''
            }`;

            if (
              (firstGroup && secondGroup && isShowSecondGroup) ||
              (firstGroup && thirdGroup && isShowThirdGroup) ||
              (secondGroup && thirdGroup && isShowSecondGroup && isShowThirdGroup)
            ) {
                return setSearch(
                  `${first?.length > 1 ? `(${firstGroup})` : firstGroup} ${
                    secondGroup && isShowSecondGroup ? `AND ${second?.length > 1 ? `(${secondGroup})` : secondGroup}` : ''
                  } ${
                    thirdGroup && isShowThirdGroup ? `AND ${third?.length > 1 ? `(${thirdGroup})` : thirdGroup}` : ''
                  } ${excludeGroup}`
                );
            } else {
                return setSearch((firstGroup || secondGroup || thirdGroup) + ' ' + excludeGroup);
            }
        }
    }, [advancedSearch, isAnyFirstGroup, isAnySecondGroup, isAnyThirdGroup, isShowSecondGroup, isShowThirdGroup, isAdvancedSearchActive]);

    useEffect(() => {
        if (searchValue) {
            const regexForGroup = /\(([^)]+)\)/g;
            const matchesGroup = searchValue.match(regexForGroup);

            if (matchesGroup?.length) {
                if (matchesGroup?.length > 3) {
                    setErrorMessage('The group limit has been exceeded. The maximum number of groups should not exceed three');
                    setIsHasError(true);
                } else {
                    setErrorMessage('');
                    setIsHasError(false);
                }
            } else {
                setErrorMessage('');
                setIsHasError(false);
            }
        }
    }, [searchValue]);

    const findKeywords = () => {
        const regexForGroup = /\(([^)]+)\)/g;
        const matchesGroup = searchValue.match(regexForGroup);
        const excludeWordsRegex = /-(?:\w+|"[^"]*")/g;
        const matchesExcludeWords = searchValue.match(excludeWordsRegex);
        const operatorRegex = /\b(AND|OR)\b/g;
        const duplicateSpacesRegex = /\s+/g;
        let firstGroup = '';
        let secondGroup = '';
        let thirdGroup = '';
        const excludeWords = matchesExcludeWords?.map((item) => item.substring(1));

        if (matchesGroup?.length) {
            firstGroup = matchesGroup[0];
            secondGroup = matchesGroup?.length > 1 ? matchesGroup[1] : '';
            thirdGroup = matchesGroup?.length > 2 ? matchesGroup[2] : '';
        }

        const wordsOutsideGroups = searchValue
            .replace(regexForGroup, '')
            .replace(excludeWordsRegex, '')
            .replace(operatorRegex, '')
            .replace(duplicateSpacesRegex, ' ')
            .trim()
            .split(' ');

        const { groupItems: firstGroupItems, isAny: isAnyOperatorInFirstGroup } = getItemsForGroup(firstGroup);
        const { groupItems: secondGroupItems, isAny: isAnyOperatorInSecondGroup } = getItemsForGroup(secondGroup);
        const { groupItems: thirdGroupItems, isAny: isAnyOperatorInThirdGroup } = getItemsForGroup(thirdGroup);

        const isHasWordsOutsideGroup = wordsOutsideGroups?.length && !!wordsOutsideGroups[0];
        let isNeedReplaceGroup = false;

        if (firstGroupItems?.length && isHasWordsOutsideGroup) {
            let firstMatchIndex = searchValue.length;

            firstGroupItems.forEach((word) => {
                const index = searchValue.indexOf(word);
                if (index !== -1 && index < firstMatchIndex) {
                    firstMatchIndex = index;
                }
            });

            wordsOutsideGroups.forEach((word) => {
                const index = searchValue.indexOf(word);
                if (index !== -1 && index < firstMatchIndex) {
                    firstMatchIndex = index;
                }
            });

            if (firstMatchIndex < searchValue.length) {
                isNeedReplaceGroup = !(
                    firstMatchIndex === searchValue.indexOf(firstGroupItems[0]) || firstMatchIndex === searchValue.indexOf(firstGroupItems[1])
                );
            }
        }

        if (!firstGroupItems?.length && wordsOutsideGroups) {
            setIsAnyFirstGroup(
                searchValue
                    .replace(regexForGroup, '')
                    .replace(excludeWordsRegex, '')
                    ?.split(' ')
                    ?.find((item) => item === 'OR') === 'OR'
            );
        }

        firstGroupItems?.length && !isNeedReplaceGroup && setIsAnyFirstGroup(isAnyOperatorInFirstGroup);
        isNeedReplaceGroup
            ? setIsAnySecondGroup(isAnyOperatorInFirstGroup)
            : secondGroupItems?.length && setIsAnySecondGroup(isAnyOperatorInSecondGroup);
        isNeedReplaceGroup
          ? setIsAnyThirdGroup(isAnyOperatorInSecondGroup)
          : thirdGroupItems?.length && setIsAnyThirdGroup(isAnyOperatorInThirdGroup);
        setIsShowSecondGroup(!!((firstGroupItems?.length && isHasWordsOutsideGroup) || secondGroupItems?.length));
        setIsShowThirdGroup(!!((firstGroupItems?.length && secondGroupItems?.length && isHasWordsOutsideGroup) || thirdGroupItems?.length));

        setAdvancedSearch({
            first: firstGroupItems?.length ? (isNeedReplaceGroup ? wordsOutsideGroups : firstGroupItems) : wordsOutsideGroups || [],
            second:
                wordsOutsideGroups && firstGroupItems?.length && !secondGroupItems?.length
                    ? isNeedReplaceGroup
                        ? firstGroupItems
                        : wordsOutsideGroups
                    : searchValue.indexOf(firstGroupItems[0]) > 1 ? firstGroupItems : secondGroupItems,
            third:
                wordsOutsideGroups && firstGroupItems?.length && secondGroupItems.length && !thirdGroupItems?.length
                    ? isNeedReplaceGroup
                        ? secondGroupItems
                        : wordsOutsideGroups
                    : thirdGroupItems,
            exclude: excludeWords,
        });

        setIsHasError(false);
    };

    const getItemsForGroup = (arr) => {
        if (!arr?.length) return { groupItems: [], isAny: false };

        const wordsInGroup = arr.match(/\w+|"[^"]*"/g) || [];
        const groupItems = wordsInGroup.filter((item) => item !== 'AND' && item !== 'OR');
        const isAny = wordsInGroup.find((word) => word === 'OR');

        return { groupItems, isAny };
    };

    const stringSeparator = (str, prevValue) => {
        const isDeletedSymbol = prevValue?.length > str?.length;
        const spaceEndStr = /\s$/.test(str);
        const repeatSpaces = /\s\s+$/.test(str);

        if (repeatSpaces && spaceEndStr && !isDeletedSymbol) return [];
        if (isDeletedSymbol && str?.length === 0) return 0;

        const regExp = /("[^"]*("|$)|\S+)/g;
        const result = [];
        let match;

        while ((match = regExp.exec(str)) !== null) {
            result.push(match[0]?.replace(/(\([^)]*\))|(\s{2,})/g, (match, group1) => group1 || ' '));
        }

        spaceEndStr && !isDeletedSymbol && result.push('');

        return result;
    };

    const handleChangeAdvancedSearch = (type, str, prevValue) => {
        const value = stringSeparator(str, prevValue);
        value === 0 && setAdvancedSearch({ ...advancedSearch, [type]: [] });
        value?.length && setAdvancedSearch({ ...advancedSearch, [type]: value });
    };

    const handleSubmitAdvancedSearch = () => {
        setSearchValue(search.trim());
        handleCreatePreview(search.trim());
    };

    const handleToggleAdvancedSearch = () => {
        if (!isAdvancedSearchActive) {
            errorMessage &&
                setErrorMessage(
                    'The group limit has been exceeded. The maximum number of groups should not exceed three. The extra groups were omitted'
                );
            findKeywords();
        } else {
            errorMessage && setErrorMessage('The group limit has been exceeded. The maximum number of groups should not exceed three');
            setSearchValue(search.trim());
        }
        setIsAdvancedSearchActive(!isAdvancedSearchActive);
        setIsAdvancedSearchShown(!isAdvancedSearchActive);
    };

    return (
        <Row>
            <Col xs={8} md={8}>
                {isAdvancedSearchActive && (
                    <div style={{ marginBottom: '10px' }}>
                        Search value: <b>{search}</b>
                    </div>
                )}
                {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
                <div className="internal-search-form">
                    {isAdvancedSearchActive ? (
                        <div style={{ width: '100%' }}>
                            <InputGroup className="d-flex">
                                <Input
                                    required
                                    value={firstGroupValue}
                                    onChange={(e) => handleChangeAdvancedSearch('first', e?.target?.value, firstGroupValue)}
                                    placeholder="FIRST GROUP*"
                                />
                                <div className="d-flex flex-column ml-3" style={{ width: '125px' }}>
                                    <RadioInput onChange={() => setIsAnyFirstGroup(true)} checked={isAnyFirstGroup} name="anyFirst" text="Any of" />
                                    <RadioInput onChange={() => setIsAnyFirstGroup(false)} checked={!isAnyFirstGroup} name="allFirst" text="All of" />
                                </div>
                            </InputGroup>
                            {previewError && <div style={{ color: 'red', marginBottom: '20px' }}>{previewError}</div>}

                            <Button color="light" style={{ cursor: 'pointer' }} onClick={() => setIsShowSecondGroup(!isShowSecondGroup)}>
                                {isShowSecondGroup ? 'Remove' : 'Add'} Second Group
                                {isShowSecondGroup ? <i className="fa fa-minus ml-1" /> : <i className="fa fa-plus ml-1" />}
                            </Button>

                            {isShowSecondGroup && (
                                <InputGroup className="d-flex">
                                    <Input
                                        value={secondGroupValue}
                                        onChange={(e) => handleChangeAdvancedSearch('second', e?.target?.value, secondGroupValue)}
                                        placeholder="SECOND GROUP"
                                    />

                                    <div className="d-flex flex-column ml-3" style={{ width: '125px' }}>
                                        <RadioInput
                                            onChange={() => setIsAnySecondGroup(true)}
                                            checked={isAnySecondGroup}
                                            name="anySecond"
                                            text="Limited by Any"
                                        />

                                        <RadioInput
                                            onChange={() => setIsAnySecondGroup(false)}
                                            checked={!isAnySecondGroup}
                                            name="allSecond"
                                            text="Limited by All"
                                        />
                                    </div>
                                </InputGroup>
                            )}

                            {isShowSecondGroup &&
                                <Button color="light" style={{ cursor: 'pointer', display: "inline-block" }} onClick={() => setIsShowThirdGroup(!isShowThirdGroup)}>
                                    {isShowThirdGroup ? 'Remove' : 'Add'} Third Group
                                    {isShowThirdGroup ? <i className="fa fa-minus ml-1"/> : <i className="fa fa-plus ml-1"/>}
                                </Button>
                            }

                            {isShowThirdGroup && (
                                <InputGroup className="d-flex">
                                    <Input
                                        value={thirdGroupValue}
                                        onChange={(e) => handleChangeAdvancedSearch('third', e?.target?.value, thirdGroupValue)}
                                        placeholder="THIRD GROUP"
                                    />

                                    <div className="d-flex flex-column ml-3" style={{ width: '125px' }}>
                                        <RadioInput
                                            onChange={() => setIsAnyThirdGroup(true)}
                                            checked={isAnyThirdGroup}
                                            name="anyThird"
                                            text="Limited by Any"
                                        />

                                        <RadioInput
                                            onChange={() => setIsAnyThirdGroup(false)}
                                            checked={!isAnyThirdGroup}
                                            name="allThird"
                                            text="Limited by All"
                                        />
                                    </div>
                                </InputGroup>
                            )}

                            <InputGroup className="d-flex mt-3">
                                <Input
                                    value={excludeValue}
                                    onChange={(e) => handleChangeAdvancedSearch('exclude', e?.target?.value, excludeValue)}
                                    placeholder="Remove these keywords and phrases"
                                />

                                <div className="ml-3" style={{ width: '125px' }} />
                            </InputGroup>
                            {children}
                        </div>
                    ) : (
                        <InputGroup className="internal-search-form__keyword">
                            <TextareaAutocomplete
                                handleInternalSearchValue={handleInternalSearchValue}
                                onPressEnter={onPressEnter}
                                value={searchValue}
                                searchValueHasError={searchValueError}
                                setSearchValueHasError={setSearchValueError}
                                onSubmit={handleCreatePreview}
                                onClear={resetForm}
                                setErrors={setErrors}
                                handleAutocompleteSelect={handleAutocompleteSelect}
                                error={errorObj && errorObj.errors.hasOwnProperty('keyword')}
                                preSubmitError={preSubmitError}
                                setPreSubmitError={setPreSubmitError}
                            />

                            <div className="mt-1" style={{ textAlign: 'right' }}>
                                <CharacterCount value={searchValue} limit={2048} />
                            </div>

                            {previewError && <div style={{ color: 'red', marginLeft: '20px' }}>{previewError}</div>}
                        </InputGroup>
                    )}

                    {isShowCategories ? (
                        <FormGroup
                            className="internal-search-form__category"
                            style={{
                                flex: `0 0 ${dropdownWidth + 8 > 155 ? dropdownWidth + 8 : 155}px`,
                            }}>
                            {categoriesLoading ? (
                                <Input type="select">
                                    <option disabled>Fetching categories...</option>
                                </Input>
                            ) : (
                                <Input
                                    type="select"
                                    name="categorySelect"
                                    id="categorySelect"
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    value={String(selectedCategory)}>
                                    <>
                                        <option>All AI Topics</option>

                                        {categories?.map((item) => (
                                            <optgroup key={item.type} label={item.type}>
                                                {item.categories.map((category) => (
                                                    <option key={category.id} value={String(category.id)}>
                                                        {category.name} - {item.type}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        ))}
                                    </>
                                </Input>
                            )}
                        </FormGroup>
                    ) : null}
                </div>

                {preSubmitError && preSubmitError.errors.hasOwnProperty('keyword') && (
                    <div className="mt-1 error-text">{preSubmitError.errors['keyword'][0].message || preSubmitError.errors['keyword']}</div>
                )}

                {errorObj && errorObj.errors.hasOwnProperty('keyword') && (
                    <div className="mt-1 error-text">{errorObj.errors['keyword'][0].message || errorObj.errors['keyword']}</div>
                )}
            </Col>

            <Col
                xs={2}
                md={2}
                className={'pl-0 ' + (isAdvancedSearchActive ? '' : ' mt-2')}
                style={isAdvancedSearchActive ? { marginTop: '2.5rem' } : {}}>
                <InfoBlock />
            </Col>

            {!titleFormVisible && !isAdvancedSearchActive && <div className="ml-3">{children}</div>}

            <Col xs={12}>
                <div className="alert-form-utility">
                    <div className="alert-form-utility__inner">
                        <FormGroup check inline>
                            <Label check>
                                <Input type="checkbox" name="inTitlesOnly" checked={inTitlesOnly} onChange={() => setInTitlesOnly(!inTitlesOnly)} />
                                Headlines
                            </Label>
                        </FormGroup>

                        {/*{selectedAlerts.length < 1 && (*/}
                        {/*    <FormGroup check inline>*/}
                        {/*        <Label check>*/}
                        {/*            <Input*/}
                        {/*                type="checkbox"*/}
                        {/*                name="useGoogleAlerts"*/}
                        {/*                checked={useGoogleAlerts}*/}
                        {/*                onChange={() => setUseGoogleAlerts(!useGoogleAlerts)}*/}
                        {/*            />*/}
                        {/*            All Sources*/}
                        {/*        </Label>*/}
                        {/*    </FormGroup>*/}
                        {/*)}*/}

                        {/* OZNEWS-3136: "-stock news" (stop-words) disabled for all searches. Uncomment to restore (also re-add UncontrolledTooltip to the reactstrap import above). */}
                        {/*<FormGroup id="stock-news" check inline>*/}
                        {/*    <Label check>*/}
                        {/*        <Input type="checkbox" name="useStopWords" checked={useStopWords} onChange={() => setUseStopWords(!useStopWords)} />*/}
                        {/*        -stock news*/}
                        {/*    </Label>*/}
                        {/*</FormGroup>*/}
                        {/*<UncontrolledTooltip placement="bottom" target="stock-news" delay={{ show: 300, hide: 0 }}></UncontrolledTooltip>*/}

                        <div className="form-group mb-0">
                            {selectedAlerts.length > 0 ? (
                                <div className="alert-form-utility__select-alerts">
                                    <div className="alert-name-span" onClick={toggleSelectAlertsModal}>
                                        {selectedAlerts.length} {selectedAlerts.length > 1 ? 'Alerts' : 'Alert'}
                                    </div>
                                    <div className="closebtn" onClick={() => handleClearAlerts()}>
                                        <span>×</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="alert-form-utility__select-alerts" onClick={toggleSelectAlertsModal}>
                                    <span>
                                        Select Sources <i className="fa fa-caret-down" style={{ marginLeft: '.5rem' }} />
                                    </span>
                                </div>
                            )}
                        </div>

                        {!!isHasAccessAdvancedSearch && (
                            <label style={{ display: 'flex' }}>
                                <Toggle checked={isAdvancedSearchActive} icons={false} onChange={handleToggleAdvancedSearch} />
                                <span style={{ marginLeft: '10px' }}>Advanced Search</span>
                            </label>
                        )}
                    </div>
                </div>

                {isAdvancedSearchActive && !isEditForm && (
                    <Button disabled={isHasError} style={{ marginTop: '15px' }} onClick={handleSubmitAdvancedSearch} color="primary" className="mr-2">
                        Search
                    </Button>
                )}
            </Col>

            <SelectAlertsModal
                isOpen={selectAlertsModalOpen}
                toggle={toggleSelectAlertsModal}
                selectedList={selectedAlerts}
                setSelectedAlerts={setSelectedAlerts}
                clearPreviewForm={clearPreviewForm}
                handleAlertSelectSubmit={handleAlertSelectSubmit}
                handleError={handleError}
                currentId={currentId}
            />
        </Row>
    );
};
